/**
 * ts-main-style MasterLayout for fleet-doctor.
 * Replaces Metronic's MasterLayout with ts-main's layout pattern:
 * - Dark navy sidebar (#0f172a) with lucide-react icons
 * - White header with search, user dropdown
 * - Floating bottom navigation
 * - Minimal footer
 *
 * Preserves all fleet-doctor business logic:
 * - MachineryUploadModal (event-driven)
 * - MachineryUploadJobBanner
 * - Outlet for React Router child routes
 *
 * Backward-compatible provider wrappers from old Metronic MasterLayout:
 * - PageDataProvider (page title/breadcrumbs context)
 * - ThemeModeProvider (dark/light mode context)
 */
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import FloatingNav from './FloatingNav'
import Footer from './Footer'
import { MachineryUploadJobBanner } from '../../app/modules/Technical/components/Machinery/MachineryUploadJobBanner'
import { MachineryUploadModal } from '../../app/modules/Technical/components/Machinery/MachineryUploadModal'
import { PageDataProvider } from '../../_metronic/layout/core'
import { ThemeModeProvider } from '../../_metronic/partials'
import VesselLandingProvider from '../../app/shared/components/VesselLandingProvider'
import { GlobalFilterProvider } from '../../app/shared/context/GlobalFilterContext'

const MasterLayout = () => {
  const location = useLocation()
  const [showMachineryUploadModal, setShowMachineryUploadModal] = useState(false)

  // Preserve MachineryUploadModal event listener (business logic)
  useEffect(() => {
    const handler = () => setShowMachineryUploadModal(true)
    window.addEventListener('open-machinery-upload-modal', handler)
    return () => window.removeEventListener('open-machinery-upload-modal', handler)
  }, [])

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <PageDataProvider>
      <ThemeModeProvider>
        <VesselLandingProvider>
        <GlobalFilterProvider>
        <div className="flex h-screen w-full overflow-hidden bg-[#eef2f7]">
          {/* Sidebar — ts-main dark navy */}
          <Sidebar />

          {/* Main content area */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Header — ts-main white */}
            <Header />

            {/* Content area with scroll */}
            <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6">
              <MachineryUploadJobBanner />
              <Outlet />
            </main>

            {/* Footer */}
            <Footer />
          </div>

          {/* Floating bottom navigation */}
          <FloatingNav />

          {/* Business logic modals (preserved from original MasterLayout) */}
          <MachineryUploadModal
            visible={showMachineryUploadModal}
            onClose={() => setShowMachineryUploadModal(false)}
          />
        </div>
        </GlobalFilterProvider>
        </VesselLandingProvider>
      </ThemeModeProvider>
    </PageDataProvider>
  )
}

export { MasterLayout }
